import React, { useState, useEffect } from "react";
import {
  FileText,
  CheckCircle,
  Clock,
  Share,
  Archive,
  Upload,
  Lock,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import documentService from "../../services/document.service";
import Loading from "../Common/Loading";
import styles from "./Dashboard.module.css";
import modulesService, { Module } from "../../services/modules.service";
import authService from "../../services/auth.service";

const UserDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total: 0,
    processed: 0,
    processing: 0,
    sharedWithMe: 0,
    archived: 0,
  });
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<Module[]>([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const user = authService.getUser();
        if (!user?.email) return;

        const data = await modulesService.getUserModules(user.email);
        setModules(data);
      } catch (err) {
        console.error(err);
      } finally {
        setPermissionsLoaded(true);
      }
    };

    loadPermissions();
  }, []);

  const dashModule = modules.find((m) => m.name === "dashboard");

  const hasRead = dashModule?.privileges?.includes("READ");

  const canViewDashboard = permissionsLoaded && hasRead;

  const canCreateDashboard =
    hasRead && dashModule?.privileges?.includes("CREATE");
  const docmodule = modules.find((m) => m.name === "documents");

  const canViewDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("READ") ?? false);
  const canCreateDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("CREATE") ?? false);
  const canEditDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("UPDATE") ?? false);
  const canShareDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("SHARE") ?? false);
  const canDownloadDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("EXPORT") ?? false);
  const canDeleteDocument =
    permissionsLoaded && (docmodule?.privileges?.includes("DELETE") ?? false);
  useEffect(() => {
    if (!permissionsLoaded || !canViewDashboard) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        const statsRes = await documentService.getStats();
        setStats(statsRes);
      } catch (error) {
        console.error("Failed to load stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [permissionsLoaded, canViewDashboard]);

  if (!permissionsLoaded || loading) return <Loading />;

  if (!canViewDashboard) {
    return (
      <div className={styles.dashboardContainer}>
        <div style={{ textAlign: "center", padding: "80px", color: "#64748b" }}>
          <h2>No Access</h2>
          <p>You do not have permission to view dashboard.</p>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.dashboardContainer}>
      <div className={styles.titleSection}>
        <h1>Document Dashboard</h1>
        <p>Overview of your document processing</p>
      </div>

      <div className={styles.kpiGrid}>
        <div
          className={styles.kpiCard}
          onClick={() => {
            if (!canViewDocument) return;
            navigate("/documents");
          }}
        >
          <div
            className={`${styles.kpiIcon}`}
            style={{ backgroundColor: "#dbeafe", color: "#1d4ed8" }}
          >
            <FileText size={16} />
          </div>
          <div className={styles.kpiInfo}>
            <span className={styles.kpiValue}>{stats.total}</span>
            <span className={styles.kpiLabel}>Total Documents</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div
            className={`${styles.kpiIcon}`}
            style={{ backgroundColor: "#dcfce7", color: "#166534" }}
          >
            <CheckCircle size={16} />
          </div>
          <div className={styles.kpiInfo}>
            <span className={styles.kpiValue}>{stats.processed}</span>
            <span className={styles.kpiLabel}>Processed</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div
            className={`${styles.kpiIcon}`}
            style={{ backgroundColor: "#fed7aa", color: "#c2410c" }}
          >
            <Clock size={16} />
          </div>
          <div className={styles.kpiInfo}>
            <span className={styles.kpiValue}>{stats.processing}</span>
            <span className={styles.kpiLabel}>Processing</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div
            className={`${styles.kpiIcon}`}
            style={{ backgroundColor: "#f0f9ff", color: "#0ea5e9" }}
          >
            <Share size={16} />
          </div>
          <div className={styles.kpiInfo}>
            <span className={styles.kpiValue}>{stats.sharedWithMe}</span>
            <span className={styles.kpiLabel}>Shared with Me</span>
          </div>
        </div>

        <div className={styles.kpiCard}>
          <div
            className={`${styles.kpiIcon}`}
            style={{ backgroundColor: "#f3f4f6", color: "#6b7280" }}
          >
            <Archive size={16} />
          </div>
          <div className={styles.kpiInfo}>
            <span className={styles.kpiValue}>{stats.archived}</span>
            <span className={styles.kpiLabel}>Archived</span>
          </div>
        </div>
      </div>

      <div className={styles.actionsCard}>
        <h3>Quick Actions</h3>
<div className={styles.actionButtons}>
  {canCreateDocument || canViewDocument ? (
    <>
      {canCreateDocument && (
        <button
          className={styles.actionBtn}
          onClick={() => navigate("/documents/upload")}
        >
          <Upload size={16} />
          Upload Document
        </button>
      )}

      {canViewDocument && (
        <button
          className={styles.actionBtn}
          onClick={() => navigate("/documents")}
        >
          <FileText size={16} />
          View All Documents
        </button>
      )}
    </>
  ) : (
    <div className={styles.noAccessPlaceholder}>
      {/* <Upload size={16} /> */}
        <Lock size={16} />
      <span>No actions available</span>
    </div>
  )}
</div>
      </div>
    </div>
  );
};

export default UserDashboard;
